import React, { useState } from "react";
import { Link } from "react-router-dom";

const ArrowSVG = () => (
  <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5.22852" y="17.3296" width="17.888" height="2.27149" transform="rotate(-40.2798 5.22852 17.3296)" fill="white" />
    <rect x="7.75781" y="6.25635" width="2.27149" height="2.27149" transform="rotate(-40.2798 7.75781 6.25635)" fill="white" />
    <rect x="10.959"  y="6.52002" width="2.27149" height="2.27149" transform="rotate(-40.2798 10.959 6.52002)"  fill="white" />
    <rect x="14.1562" y="6.78516" width="2.27149" height="2.27149" transform="rotate(-40.2798 14.1562 6.78516)" fill="white" />
    <rect x="17.0957" y="10.25"   width="2.27149" height="2.27149" transform="rotate(-40.2798 17.0957 10.25)"   fill="white" />
    <rect x="16.832"  y="13.4517" width="2.27149" height="2.27149" transform="rotate(-40.2798 16.832 13.4517)"  fill="white" />
    <rect x="16.5684" y="16.6538" width="2.27149" height="2.27149" transform="rotate(-40.2798 16.5684 16.6538)" fill="white" />
  </svg>
);

type Lang = "curl" | "js" | "python" | "java" | "swift" | "php";

const snippets: Record<Lang, string> = {
  curl: `curl --request POST \\
  --url https://api.trustengine.co.id/v1/ekyc/verify \\
  --header 'Authorization: Bearer YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
  "selfie_image": "base64_encoded_image",
  "id_card_image": "base64_encoded_image",
  "id_type": "KTP"
}'`,

  js: `const response = await fetch(
  'https://api.trustengine.co.id/v1/ekyc/verify',
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      selfie_image: base64Image,
      id_card_image: base64IdCard,
      id_type: 'KTP',
    }),
  }
);
const result = await response.json();`,

  python: `import requests

response = requests.post(
  'https://api.trustengine.co.id/v1/ekyc/verify',
  headers={
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json',
  },
  json={
    'selfie_image': base64_image,
    'id_card_image': base64_id_card,
    'id_type': 'KTP',
  }
)
result = response.json()`,

  java: `OkHttpClient client = new OkHttpClient();

String body = "{"
  + "\\"selfie_image\\": \\"base64_image\\","
  + "\\"id_card_image\\": \\"base64_id\\","
  + "\\"id_type\\": \\"KTP\\""
  + "}";

Request request = new Request.Builder()
  .url("https://api.trustengine.co.id/v1/ekyc/verify")
  .addHeader("Authorization", "Bearer YOUR_API_KEY")
  .post(RequestBody.create(body,
    MediaType.parse("application/json")))
  .build();`,

  swift: `let url = URL(
  string: "https://api.trustengine.co.id/v1/ekyc/verify"
)!
var request = URLRequest(url: url)
request.httpMethod = "POST"
request.setValue(
  "Bearer YOUR_API_KEY",
  forHTTPHeaderField: "Authorization"
)
let body: [String: Any] = [
  "selfie_image": base64Image,
  "id_card_image": base64IdCard,
  "id_type": "KTP"
]
request.httpBody = try? JSONSerialization
  .data(withJSONObject: body)`,

  php: `$client = new \\GuzzleHttp\\Client();

$response = $client->post(
  'https://api.trustengine.co.id/v1/ekyc/verify',
  [
    'headers' => [
      'Authorization' => 'Bearer YOUR_API_KEY',
      'Content-Type'  => 'application/json',
    ],
    'json' => [
      'selfie_image'  => $base64Image,
      'id_card_image' => $base64IdCard,
      'id_type'       => 'KTP',
    ],
  ]
);
$result = json_decode($response->getBody());`,
};

const tabs: { key: Lang; label: string; icon: string }[] = [
  { key: "curl",   label: "cURL",   icon: "//" },
  { key: "js",     label: "JS",     icon: "JS" },
  { key: "java",   label: "Java",   icon: "☕" },
  { key: "swift",  label: "Swift",  icon: "◆" },
  { key: "python", label: "Python", icon: "🐍" },
  { key: "php",    label: "PHP",    icon: "php" },
];

const ApiShowcaseSection: React.FC = () => {
  const [activeLang, setActiveLang] = useState<Lang>("curl");

  return (
    <section
      className="api-showcase-section pt-120 pb-120"
      style={{ background: "linear-gradient(135deg, #0a1628 0%, #0f2040 60%, #0d1e3a 100%)" }}
    >
      <div className="container">
        <div className="row align-items-center mt-none-50">

          {/* ── Left Content ── */}
          <div className="col-lg-5 mt-50">
            <div className="sec-title mb-40">
              <span className="sub-title mb-15" style={{ color: "#00c896" }}>
                Developer-First API
              </span>
              <h2 className="title" style={{ color: "#ffffff", fontSize: "38px", lineHeight: "1.25" }}>
                Integrate in minutes with our secure, compatible SDK
              </h2>
              <p style={{ color: "rgba(255,255,255,0.65)", lineHeight: "1.8", marginTop: "20px" }}>
                Build powerful verification flows with our REST API and native
                SDKs. Full documentation, sandbox environment, and dedicated
                support — go live in under an hour.
              </p>
            </div>

            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              <Link className="thm-btn agency-btn" to="/contact">
                <span className="text">View API Docs</span>
                <span className="arrow">
                  <span className="arrow-icon">
                    <ArrowSVG /><ArrowSVG />
                  </span>
                </span>
              </Link>
            </div>

            {/* Stats */}
            <div style={{ display: "flex", gap: "40px", marginTop: "50px" }}>
              {[
                { val: "<1hr",  label: "Time to integrate" },
                { val: "99.9%", label: "API uptime SLA" },
                { val: "~200ms",label: "Avg response time" },
              ].map((s, i) => (
                <div key={i}>
                  <div style={{ fontSize: "24px", fontWeight: 700, color: "#00c896" }}>{s.val}</div>
                  <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.5)", marginTop: "4px" }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right Code Block ── */}
          <div className="col-lg-7 mt-50">
            <div style={{
              background: "#111827",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}>

              {/* Language tabs */}
              <div style={{
                display: "flex",
                gap: "4px",
                padding: "12px 16px",
                background: "#0d1117",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                overflowX: "auto",
              }}>
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveLang(tab.key)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: "8px",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      background: activeLang === tab.key ? "#00c896" : "transparent",
                      color:      activeLang === tab.key ? "#0a1628"  : "rgba(255,255,255,0.5)",
                      transition: "all 0.2s",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}

                {/* Window dots */}
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "6px" }}>
                  {["#ff5f57","#febc2e","#28c840"].map((c, i) => (
                    <span key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: c, display: "block" }} />
                  ))}
                </div>
              </div>

              {/* Code */}
              <pre style={{
                margin: 0,
                padding: "28px 28px",
                fontSize: "13.5px",
                lineHeight: "1.75",
                color: "#e2e8f0",
                fontFamily: "'Fira Code', 'Courier New', monospace",
                overflowX: "auto",
                minHeight: "300px",
                background: "#111827",
              }}>
                <code
                  dangerouslySetInnerHTML={{
                    __html: snippets[activeLang]
                      .replace(/--[\w-]+/g,       '<span style="color:#60a5fa">$&</span>')
                      .replace(/'[^']*'/g,         '<span style="color:#86efac">$&</span>')
                      .replace(/"[^"]*"/g,         '<span style="color:#fcd34d">$&</span>')
                      .replace(/\b(POST|GET|curl|await|fetch|import|return|const|let|var|def|class|new|try|json)\b/g,
                                                   '<span style="color:#c084fc">$&</span>')
                      .replace(/\b(Bearer|Authorization|Content-Type)\b/g,
                                                   '<span style="color:#f97316">$&</span>'),
                  }}
                />
              </pre>

              {/* Response preview */}
              <div style={{
                padding: "12px 24px",
                background: "#0d1117",
                borderTop: "1px solid rgba(255,255,255,0.06)",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}>
                <span style={{
                  padding: "2px 10px",
                  background: "rgba(0,200,150,0.15)",
                  color: "#00c896",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: 700,
                }}>200 OK</span>
                <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "12px" }}>
                  {`{ "status": "verified", "match_score": 98.4, "liveness": true }`}
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ApiShowcaseSection;
